import { useRef, useState } from "react";
import { Link } from "react-router";
import { api, ApiError } from "../api/client";
import { ErrorNote } from "../components/ErrorNote";
import { useCampaign, useCampaignQuery } from "./context";
import type { CampaignAttachment, CampaignEntryListItem, CampaignField, CampaignFieldSpec, Visibility } from "./types";

/** Marks a secret part; only the dossier and the material of a scene show secret parts. */
export function SecretBadge({ visibility }: { visibility: Visibility }) {
  const { mode } = useCampaign();
  if (visibility !== "secret") return null;
  // Outside the dossier the game master view sees a secret part only as material of the active scene.
  return <span className="badge secret">{mode === "normal" ? "Szenenmaterial" : "geheim"}</span>;
}

/** Switches a part between known and secret; only in the dossier, where both are visible. */
export function VisibilityToggle(props: { visibility: Visibility; label: string; onChange: (visibility: Visibility) => Promise<unknown> }) {
  const { mode } = useCampaign();
  const [busy, setBusy] = useState(false);
  if (mode !== "dossier") return <SecretBadge visibility={props.visibility} />;
  const next: Visibility = props.visibility === "secret" ? "known" : "secret";
  return (
    <button
      type="button"
      className={`chip small visibility ${props.visibility}`}
      disabled={busy}
      aria-label={`${props.label}: ${props.visibility === "secret" ? "geheim" : "bekannt"}, umschalten`}
      onClick={async () => {
        setBusy(true);
        try {
          await props.onChange(next);
        } finally {
          setBusy(false);
        }
      }}
    >
      {props.visibility === "secret" ? "geheim" : "bekannt"}
    </button>
  );
}

/** An error of the campaign; a conflict, and with reload any failed load, offers to load the current state. */
export function CampaignError({ error, onClose, reload = false }: { error: Error | null; onClose?: () => void; reload?: boolean }) {
  const campaign = useCampaign();
  if (!error) return null;
  return (
    <div className="stack campaign-error">
      <ErrorNote error={error} />
      {(reload || (error instanceof ApiError && error.status === 409)) && (
        <button
          type="button"
          className="button secondary"
          onClick={() => {
            onClose?.();
            campaign.reload();
          }}
        >
          Neu laden
        </button>
      )}
    </div>
  );
}

/** Runs an action and keeps its error until the next one; while one runs, another click or Enter does nothing. */
export function useAction() {
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const run = async (action: () => Promise<unknown>): Promise<boolean> => {
    if (running.current) return false;
    running.current = true;
    setError(null);
    setBusy(true);
    try {
      await action();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
      return false;
    } finally {
      running.current = false;
      setBusy(false);
    }
  };
  return { error, busy, run, clear: () => setError(null) };
}

/** The entries of the campaign and its world that the view sees, optionally of one kind, tag or place. */
export function useEntries(filter: { kind?: string; tag?: string; scope?: "campaign" | "world" } = {}) {
  const { id } = useCampaign();
  return useCampaignQuery(["entries", filter.kind ?? null, filter.tag ?? null, filter.scope ?? null], (headers) =>
    api.GET("/api/campaigns/{id}/entries", { params: { path: { id }, query: { kind: filter.kind, tag: filter.tag, scope: filter.scope } }, headers }),
  );
}

/** An entry with all the view sees of it; without an id nothing is loaded. */
export function useEntry(entryId: string | null) {
  const { id } = useCampaign();
  return useCampaignQuery(
    ["entry", entryId],
    (headers) => api.GET("/api/campaigns/{id}/entries/{entryId}", { params: { path: { id, entryId: entryId ?? "" } }, headers }),
    !!entryId,
  );
}

/** The fixed section „summary“ of a session or an adventure, as the view sees it. */
export function useSummary(entryId: string) {
  const { id } = useCampaign();
  const query = useCampaignQuery(["entry", entryId, "summary"], (headers) =>
    api.GET("/api/campaigns/{id}/entries/{entryId}", { params: { path: { id, entryId }, query: { sections: "summary" } }, headers }),
  );
  return { ...query, section: query.data?.sections[0] };
}

export function EntryLink({ entry }: { entry: Pick<CampaignEntryListItem, "id" | "title"> }) {
  const campaign = useCampaign();
  return <Link to={campaign.entryPath(entry.id)}>{entry.title}</Link>;
}

/** The text of a field value as the view shows it. */
export function fieldText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

/** A field's value as the view shows it: choices by their label, entries and party members by name, game days as dates. */
export function FieldValue({ field, spec }: { field: CampaignField; spec: CampaignFieldSpec | undefined }) {
  const campaign = useCampaign();
  const entries = useEntries();
  const title = (id: unknown) => entries.data?.find((e) => e.id === id)?.title ?? "…";
  const value = field.value;
  switch (field.type) {
    case "choice":
      return <>{spec?.choices.find((c) => c.id === value)?.label.de ?? fieldText(value)}</>;
    case "entry":
      return <Link to={campaign.entryPath(String(value))}>{title(value)}</Link>;
    case "partyMembers":
      return <>{(Array.isArray(value) ? value : []).map((id) => campaign.head.party.find((p) => p.id === id)?.name ?? "?").join(", ")}</>;
    case "references":
      return (
        <ul className="plain-list">
          {(Array.isArray(value) ? (value as { entryId: string }[]) : []).map((r, i) => (
            <li key={i}>
              <Link to={campaign.entryPath(r.entryId)}>{title(r.entryId)}</Link>
            </li>
          ))}
        </ul>
      );
    case "day":
      return <>{field.day?.date.de ?? fieldText(value)}</>;
    default:
      return <span className="pre-line">{fieldText(value)}</span>;
  }
}

const sizeFormat = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 });

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${sizeFormat.format(bytes / 1024)} KB`;
  return `${sizeFormat.format(bytes / (1024 * 1024))} MB`;
}

/** Opens or saves an attachment. It is fetched with the headers of the view, since a plain link would not carry the dossier. */
export function AttachmentButton({ attachment }: { attachment: Pick<CampaignAttachment, "id" | "name"> & { size?: number } }) {
  const campaign = useCampaign();
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <button
        type="button"
        className="link"
        onClick={async () => {
          setError(null);
          const view = campaign.view();
          try {
            const response = await campaign.fetch(`/api/campaigns/${campaign.id}/attachments/${attachment.id}`);
            if (!response.ok) {
              setError(response.status === 404 ? "Der Anhang ist nicht mehr da." : `Fehler ${response.status}`);
              return;
            }
            const blob = await response.blob();
            if (campaign.sameView(view)) saveBlob(blob, attachment.name);
          } catch (e) {
            // Aborted with the dossier: the file is dropped without a word, the page below is already another view.
            if (campaign.sameView(view)) setError(e instanceof Error ? e.message : String(e));
          }
        }}
      >
        {attachment.name}
      </button>{" "}
      {attachment.size != null && <span className="muted small">{fileSize(attachment.size)}</span>}
      {error && <span className="error small"> {error}</span>}
    </>
  );
}

/** Hands a file to the browser to save; the object URL is revoked right after. */
export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
